import type {
  BettingEvent,
  CampaignsType,
} from "hooks/firestore/models/bettingEvent";
import type { BettingMarketCategory } from "hooks/firestore/models/bettingMarketCategory";
import { sortByPriority } from "utilities/sortMarkets";
import type { RacingEvent } from "hooks/firestore/models/racingEvent";
import type { GroupedTokens } from "hooks/firestore/user/useTokens";
import type { Permissions } from "@/features/auth/models/permissions";
import { parseISO } from "date-fns";
import {
  getIsSGMReady,
  isLiveBlocked,
  isLiveViewOnly,
  isMarketTrading,
  shouldShowPromoBanner,
} from "utilities/sharedBettingUtilities";
import type { Maybe } from "types/utilities";
import { parseDate } from "utilities/dateUtilities";
import {
  isBettingEvent,
  type ProcessedBettingEvent,
  type ProcessedMarkets,
} from "@/hooks/firestore/betting/useEvent";
import type { Market } from "@/hooks/firestore/models/market";
import type { BettingMarketStatus } from "@/hooks/firestore/models/market";
import type { BettingType } from "@/hooks/firestore/betting/types";
import type { RaceMeeting } from "@/hooks/firestore/models/raceMeeting";
import type { RacingCompetitor } from "@/hooks/firestore/models/racingCompetitor";
import type { ProcessedRacingEvent } from "@/hooks/firestore/betting/useRacingEvents";

const regionMap = {
  au: "ANZ",
  world: "ROW",
};

/**
 * Shape produced by `processMarket` for `market.attributes`. The raw
 * firestore shape is `Record<string, any>`; processing transforms
 * `"true"` / `"false"` strings into booleans, adds the derived `custom`
 * flag, and otherwise passes attributes through. Known optional fields
 * are listed so callers can read them without casts.
 */
type ProcessedMarketAttributes = {
  custom: boolean;
  multiable?: boolean;
} & Record<string, unknown>;

/** The feed only marks the EVENT as LIVE, so a live market arrives ACTIVE.
 * Callers read LIVE off the processed market instead of pairing the two. */
const isLiveMarket = (
  status: Maybe<BettingMarketStatus>,
  bettingType: BettingType,
) => status === "ACTIVE" && bettingType === "Live";

const getMarketStatus = (
  status: Maybe<BettingMarketStatus>,
  bettingType: BettingType,
): BettingMarketStatus => {
  if (!status) {
    return "ACTIVE";
  }

  if (isLiveMarket(status, bettingType)) {
    return "LIVE";
  }

  if (["ACTIVE", "SETTLED", "CANCELLED"].includes(status)) {
    return status;
  }

  return "SUSPENDED";
};

/** Stays LIVE through suspensions until the main market settles or cancels */
const getEventStatus = (event: BettingEvent, bettingType: BettingType) => {
  const mainStatus = event.mainMarket?.status;
  const isLive = event.tab === "live" || bettingType === "Live";
  // Not DEACTIVATED, the main market flips to it mid-match
  const isClosed = mainStatus === "SETTLED" || mainStatus === "CANCELLED";

  if (isLive && !isClosed) {
    return "LIVE";
  }

  return getMarketStatus(mainStatus, bettingType);
};

const processMarket = (
  market: Market & { id: string }, // todo: find a way to get proper type here
  userCampaigns: string[],
  userTokens: GroupedTokens,
  permissions: Permissions,
) => {
  const canViewPromo = permissions["viewPromotion"] === "GRANTED";
  const promotionCampaigns = (market?.promotionCampaigns || []).filter(
    (campaign: CampaignsType) =>
      shouldShowPromoBanner(campaign, userCampaigns, userTokens, permissions),
  );

  const status = isLiveMarket(market.status, market.bettingType)
    ? "LIVE"
    : market.status;

  const attributes: ProcessedMarketAttributes = {
    // convert string of true or false to boolean
    ...Object.fromEntries(
      Object.entries(market?.attributes || {}).map(([key, value]) => {
        switch (value) {
          case "true":
            return [key, true];
          case "false":
            return [key, false];
          default:
            return [key, value];
        }
      }),
    ),
    custom: !!market?.attributes?.custom,
  };

  const augmentedOutcomes = Object.fromEntries(
    Object.entries(market.outcomes || {}).map(([outcomeId, outcome]) => [
      outcomeId,
      {
        ...outcome,
        id: outcomeId,
        market: {
          id: market.id,
          name: market.name,
          bettingType: market.bettingType,
          prematchMarket: market.prematchMarket,
          status,
          category: market.category,
          marketType: market.marketType,
          hasPromotionalMarkets: market.hasPromotionalMarkets,
        },
      },
    ]),
  );

  return {
    ...market,
    status,
    outcomes: augmentedOutcomes,
    attributes,
    nextBetStop:
      typeof market?.nextBetStop === "string"
        ? parseISO(market.nextBetStop)
        : typeof market?.nextBetStop?.toDate === "function"
          ? market.nextBetStop.toDate()
          : null,
    hasPromotionalMarkets: canViewPromo && market?.hasPromotionalMarkets,
    promotionCampaigns: canViewPromo ? promotionCampaigns : [],
    priority: 0, // default priority is 0, it will be updated when grouping markets
    showPromoBanner:
      canViewPromo &&
      (market?.hasPromotionalMarkets || promotionCampaigns.length > 0),
  };
};

const processBettingEvent = (
  event: BettingEvent,
  userCampaigns: string[],
  userTokens: GroupedTokens,
  permissions: Permissions,
) => {
  const canViewLive = permissions["viewLiveMarkets"] === "GRANTED";

  const bettingType =
    event.eventType === "MATCH" && event.mainMarket
      ? event.mainMarket.bettingType
      : "Prematch";

  const semAvailableAtDate = event.attributes?.semAvailableAtTs?.toDate();

  const promotionCampaigns = event.promotionCampaigns?.filter(
    (campaign: CampaignsType) =>
      shouldShowPromoBanner(campaign, userCampaigns, userTokens, permissions),
  );

  const canViewPromo = permissions["viewPromotion"] === "GRANTED";

  const status =
    event.eventType === "MATCH" ? getEventStatus(event, bettingType) : "ACTIVE";
  const isLive = status === "LIVE";

  // No live permission = pre-live experience, so treat it as prematch.
  const isLiveView = event.tab === "live" && canViewLive;

  const emptyCounts = { SameEventMulti: 0, SingleOutcome: 0, total: 0 };

  // marketCounts splits Live vs Prematch, so it's more accurate than
  // activeMarketCount(s). Only fall back to the old fields when the field is
  // missing entirely - a bucket is absent when it's empty, and
  // activeMarketCount counts both types.
  const counts = isLiveView
    ? event.marketCounts?.Live
    : event.marketCounts?.Prematch;

  // A live event with no live access: the only markets on offer are the live ones
  // that opened as prematch - no bucket counts those, so show no count rather
  // than one that doesn't match the page.
  const marketCounts =
    isLive && !canViewLive
      ? emptyCounts
      : event.marketCounts
        ? counts ?? emptyCounts
        : {
            ...event.activeMarketCounts,
            total: event.activeMarketCount,
          };

  return {
    ...event,
    id: event.eventId,
    activeMarketCount: marketCounts.total,
    activeMarketCounts: {
      SameEventMulti: marketCounts.SameEventMulti,
      SingleOutcome: marketCounts.SingleOutcome,
    },
    isSGMReady: getIsSGMReady(semAvailableAtDate),
    attributes: {
      ...event.attributes,
      ...(semAvailableAtDate && {
        semAvailableAtTs: semAvailableAtDate,
      }),
    },
    mainMarket: event.mainMarket
      ? processMarket(event.mainMarket, userCampaigns, userTokens, permissions)
      : undefined,
    scheduledStartTime: parseISO(event.scheduledStartTime),
    nextBetStop: event.nextBetStop?.toDate(),
    status,
    hasPromotionalMarkets: canViewPromo && event.hasPromotionalMarkets,
    promotionCampaigns: canViewPromo ? promotionCampaigns : [],
    showPromoBanner:
      canViewPromo &&
      (event.hasPromotionalMarkets || (promotionCampaigns?.length || 0) > 0),
  };
};

const filterMarkets = (
  market: ProcessedMarkets[0],
  permissions: Permissions,
  userCampaigns: string[],
  event: ProcessedBettingEvent | ProcessedRacingEvent,
): boolean => {
  // Exclude promo markets when user has no promo permissions
  if (
    market.hasPromotionalMarkets &&
    permissions["viewPromotion"] !== "GRANTED"
  ) {
    return false;
  }

  if (
    event.hub === "sports" &&
    permissions["viewSportsMarkets"] !== "GRANTED"
  ) {
    return false;
  }

  if (
    event.hub === "esports" &&
    permissions["viewEsportsMarkets"] !== "GRANTED"
  ) {
    return false;
  }

  if (
    ["HORSE_RACING", "GREYHOUNDS", "HARNESS_RACING"].includes(event.sport) &&
    permissions["viewRacingMarkets"] !== "GRANTED"
  ) {
    return false;
  }

  const marketCampaignIds = (market.promotionCampaigns || []).map((c) => c.id);
  if (
    market.campaignOnly &&
    !(userCampaigns || []).some((campaignId) =>
      (marketCampaignIds || []).includes(campaignId),
    )
  ) {
    return false;
  }

  if (
    Object.values(market.outcomes ?? {}).every(
      (outcome) => outcome.result === "VOID",
    )
  ) {
    return false;
  }

  if (
    ["sports", "esports"].includes(event.hub) &&
    Object.values(market.outcomes ?? {}).some(
      (outcome) =>
        outcome.active && outcome.odds === 0 && outcome.openingOdds === 0,
    )
  ) {
    return false;
  }

  // Hide Live markets from users without the permission. Prematch-origin ones
  // stay visible, but read-only - the odds are blanked and they aren't bettable.
  if (isLiveBlocked(market, permissions["viewLiveMarkets"] === "GRANTED")) {
    return false;
  }

  // Live tab: while the match runs, only show bettable live markets. Once it
  // finishes (no longer visible) show everything so results stay on screen.
  // Skip for users without live access so they keep the prematch view below.
  if (
    isBettingEvent(event) &&
    event.tab === "live" &&
    permissions["viewLiveMarkets"] === "GRANTED"
  ) {
    // Deactivated only comes back once settled
    if (!event.visible) {
      return market.status !== "DEACTIVATED" || event.status === "SETTLED";
    }
    // ACTIVE is defensive: `processMarket` folds a trading live market to LIVE
    // before this runs, so in the real pipeline only LIVE arrives. Kept so the
    // filter still behaves if it's ever handed a raw market.
    return (
      market.bettingType === "Live" &&
      ["ACTIVE", "LIVE", "WAITING", "SUSPENDED"].includes(market.status)
    );
  }

  // View-only keeps every prematch market while live
  if (
    isBettingEvent(event) &&
    isLiveViewOnly(
      permissions["viewLiveMarkets"] === "GRANTED",
      event.status,
      market.bettingType,
    )
  ) {
    return true;
  }

  // Deactivated only once settled
  if (isBettingEvent(event) && event.eventType === "MATCH") {
    return market.status !== "DEACTIVATED" || event.status === "SETTLED";
  }

  if (isBettingEvent(event) && isMarketTrading(event.mainMarket?.status)) {
    return market.status !== "DEACTIVATED";
  }

  return true;
};

const processRacingEvent = <T extends RacingEvent>(
  event: T,
  userCampaigns: string[],
  userTokens: GroupedTokens,
  permissions: Permissions,
) => {
  const promotionCampaigns = event.promotionCampaigns?.filter(
    (campaign: CampaignsType) =>
      shouldShowPromoBanner(campaign, userCampaigns, userTokens, permissions),
  );

  const canViewPromo = permissions["viewPromotion"] === "GRANTED";

  return {
    ...event,
    hub: "racing",
    eventName: `${event.venue} - R${event.number}`,
    eventType: "RACE",
    scheduledStartTime: parseDate(event.scheduledStartTime),
    country: event.venueCountry,
    state: event.venueState,
    trackType: event.trackType,
    promotionCampaigns: canViewPromo ? promotionCampaigns : [],
    showPromoBanner:
      canViewPromo &&
      (event.hasPromotionalMarkets || (promotionCampaigns?.length || 0) > 0),
  };
};

const processRaceMeeting = <T extends RaceMeeting>(
  meeting: T,
  userCampaigns: string[],
  userTokens: GroupedTokens,
  permissions: Permissions,
) => {
  const meetingCampaigns = meeting.promotionCampaigns?.filter(
    (campaign: CampaignsType) =>
      shouldShowPromoBanner(campaign, userCampaigns, userTokens, permissions),
  );

  const currentRaceCampaigns = meeting.currentRace.promotionCampaigns?.filter(
    (campaign: CampaignsType) =>
      shouldShowPromoBanner(campaign, userCampaigns, userTokens, permissions),
  );

  const canViewPromo = permissions["viewPromotion"] === "GRANTED";

  return {
    ...meeting,
    currentRace: {
      ...meeting.currentRace,
      hasPromotionalMarkets:
        canViewPromo && meeting.currentRace.hasPromotionalMarkets,
      promotionCampaigns: canViewPromo ? currentRaceCampaigns : [],
      showPromoBanner:
        canViewPromo &&
        (meeting.currentRace.hasPromotionalMarkets ||
          (currentRaceCampaigns?.length || 0) > 0),
    },
    hasPromotionalMarkets: canViewPromo && meeting.hasPromotionalMarkets,
    promotionCampaigns: canViewPromo ? meetingCampaigns : [],
    showPromoBanner:
      canViewPromo &&
      (meeting.hasPromotionalMarkets || (meetingCampaigns?.length || 0) > 0),
  };
};

type ProcessedMeeting = ReturnType<typeof processRaceMeeting>;

const trackTypeOrder = ["Metropolitan", "Provincial", "Country"];
const countryPriority = ["AU", "NZ", "HK"];
const statusPriority = [
  "OPEN",
  "JUMPED",
  "INTERIM",
  "PROTEST",
  "FINAL",
  "ABANDONED",
];

const sortMeetings = (a: ProcessedMeeting, b: ProcessedMeeting) => {
  const getPriority = (priorityList: string[], value: string) =>
    priorityList.includes(value) ? priorityList.indexOf(value) : 99;

  // 1. Sort by country
  const countryPriorityA = getPriority(countryPriority, a.country);
  const countryPriorityB = getPriority(countryPriority, b.country);

  if (countryPriorityA !== countryPriorityB) {
    return countryPriorityA - countryPriorityB;
  }

  // 2. Sort by track type, but only for ANZ races
  const isANZ = a.region === "ANZ" || b.region === "ANZ";
  const trackTypePriorityA = getPriority(trackTypeOrder, a.trackType || "");
  const trackTypePriorityB = getPriority(trackTypeOrder, b.trackType || "");

  if (trackTypePriorityA !== trackTypePriorityB && isANZ) {
    return trackTypePriorityA - trackTypePriorityB;
  }

  // 3. Sort by meeting status
  const statusPriorityA = getPriority(statusPriority, a.currentRace.status);
  const statusPriorityB = getPriority(statusPriority, b.currentRace.status);

  if (statusPriorityA !== statusPriorityB) {
    return statusPriorityA - statusPriorityB;
  }

  // 4. Sort by start time
  return (
    a.scheduledStartTimeTs.toDate().getTime() -
    b.scheduledStartTimeTs.toDate().getTime()
  );
};

const processRacingCompetitor = (competitor: RacingCompetitor) => {
  const startingPosition =
    competitor.startingPosition === "0" ? "" : competitor.startingPosition;

  return {
    ...competitor,
    startingPosition: startingPosition || "",
    name: competitor.name === "...." ? "Vacant Box" : competitor.name,
    weight: competitor.weight?.replace(".0", ""),
  };
};

const processBettingMarketCategory = (category: BettingMarketCategory) => ({
  name: category.name,
  priority: category.priority,
  entries: Object.values(category.entries || {})
    .map((it) => ({ ...it, priority: Number(it.priority) }))
    .sort(sortByPriority),
});

export {
  getMarketStatus,
  processMarket,
  processBettingEvent,
  processRacingEvent,
  filterMarkets,
  processRaceMeeting,
  regionMap,
  sortMeetings,
  processRacingCompetitor,
  processBettingMarketCategory,
};
